import React, { createContext, useContext, useState, useLayoutEffect } from 'react'
import { useAuth } from './auth-provider'
import { setSelectedAppId as setGlobalAppId } from '@/lib/api'
import { useNavigate, useLocation } from 'react-router-dom'

const APP_STORAGE_KEY = "selected_app_id"

interface SelectedAppContextType {
  selectedAppId: string | null
  selectedApp: any | null
  selectApp: (appId: string) => void
  apps: any[]
}

export const SelectedAppContext = createContext<SelectedAppContextType | null>(null)

export function SelectedAppProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get apps from current company context
  const currentCompany = user?.current_company
  const apps = currentCompany?.apps || []
  
  // Initialize selectedAppId synchronously
  const [selectedAppId, setSelectedAppId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const storedAppId = localStorage.getItem(APP_STORAGE_KEY)
      
      // If we have apps and a stored ID, validate it
      if (apps.length > 0) {
        if (storedAppId && apps.some(app => app.app_id === storedAppId)) {
          // Set the global API app ID
          setGlobalAppId(storedAppId)
          return storedAppId
        } else {
          // Return first app as default
          const defaultAppId = apps[0].app_id
          localStorage.setItem(APP_STORAGE_KEY, defaultAppId)
          // Set the global API app ID
          setGlobalAppId(defaultAppId)
          return defaultAppId
        }
      }
      
      // No apps yet, return stored value or null
      if (storedAppId) {
        setGlobalAppId(storedAppId)
      }
      return storedAppId
    }
    return null
  })
  
  // console.log('[SelectedAppProvider] Current state selectedAppId:', selectedAppId)
  
  // Use useLayoutEffect for synchronous updates when apps change
  useLayoutEffect(() => {
    if (apps.length > 0) {
      // Validate current selection is still valid
      if (selectedAppId && !apps.some(app => app.app_id === selectedAppId)) {
        // Current selection is not in available apps, select first
        const defaultAppId = apps[0].app_id
        setSelectedAppId(defaultAppId)
        localStorage.setItem(APP_STORAGE_KEY, defaultAppId)
        setGlobalAppId(defaultAppId)
      } else if (!selectedAppId) {
        // No selection yet, set default
        const storedAppId = localStorage.getItem(APP_STORAGE_KEY)
        
        if (storedAppId && apps.some(app => app.app_id === storedAppId)) {
          setSelectedAppId(storedAppId)
          setGlobalAppId(storedAppId)
        } else {
          const defaultAppId = apps[0].app_id
          setSelectedAppId(defaultAppId)
          localStorage.setItem(APP_STORAGE_KEY, defaultAppId)
          setGlobalAppId(defaultAppId)
        }
      } else {
        // We have a valid selected app, make sure global is in sync
        setGlobalAppId(selectedAppId)
      }
    }
  }, [apps]) // Only depend on apps
  
  const selectApp = (appId: string) => {
    // console.log('[SelectedAppProvider] selectApp called with:', appId, 'current:', selectedAppId)
    if (appId !== selectedAppId) {
      // console.log('[SelectedAppProvider] Actually updating state to:', appId)
      setSelectedAppId(appId)
      localStorage.setItem(APP_STORAGE_KEY, appId)
      setGlobalAppId(appId)
      
      // If we're on an app detail page, redirect to admin overview
      if (location.pathname.startsWith('/admin/apps/')) {
        navigate('/admin')
      }
    } else {
      // console.log('[SelectedAppProvider] Same app selected, skipping update')
    }
  }
  
  const selectedApp = apps.find(app => app.app_id === selectedAppId)
  
  return (
    <SelectedAppContext.Provider value={{
      selectedAppId,
      selectedApp,
      selectApp,
      apps
    }}>
      {children}
    </SelectedAppContext.Provider>
  )
}

export function useSelectedApp() {
  const context = useContext(SelectedAppContext)
  if (!context) {
    throw new Error('useSelectedApp must be used within SelectedAppProvider')
  }
  return context
}

// Helper to check if we're inside the provider
export function useIsInSelectedAppProvider() {
  const context = useContext(SelectedAppContext)
  return context !== null
}